import {Availability, TimeSlot} from "../../models/section/ISection";

// A section's weekly schedule, edited as seven day rows and stored in the format
// the menu and the order gate already evaluate:
// - slots are closed intervals at minute resolution: 12:00–15:00 shows through 15:00:59
// - an empty, null or absent day shows all day; a null schedule shows always
// - "not shown today" is the slot 00:00–00:01 (the evaluators still show those two minutes)
// - "until end of day" is an end of 23:59; overnight is split across two days
// - a stored slot with start > end wraps within its own day: 00:00–end and start–23:59
// encodeSchedule(decodeSchedule(x)) shows the section at the same minutes as x, except
// that stray 1-minute slots collapse into the 00:00–00:01 marker or are dropped next to
// real rows, and wrap pieces shorter than 2 minutes are dropped.

export const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
export type Day = typeof DAYS[number];
export const DAY_SHORT: Record<Day, string> = {
    monday: "Пн", tuesday: "Вт", wednesday: "Ср", thursday: "Чт", friday: "Пт", saturday: "Сб", sunday: "Вс",
};

export type DayMode = "allDay" | "hours" | "hidden";
export interface ScheduleRow {
    start: string
    end: string
    toEndOfDay: boolean
}
export interface DaySchedule {
    mode: DayMode
    rows: ScheduleRow[]
}
export interface ScheduleForm {
    enabled: boolean
    days: Record<Day, DaySchedule>
}
export interface ScheduleError {
    day: Day
    row: number | null
    message: string
}

const END_OF_DAY = 23 * 60 + 59;
const TIMEZONE = "Asia/Almaty";

// Minutes since midnight of "H:MM"/"HH:MM", or null unless it is a time of day.
function parseTime(s: string): number | null {
    const m = /^(\d{1,2}):(\d{1,2})$/.exec(s);
    if (!m || Number(m[1]) > 23 || Number(m[2]) > 59) return null;
    return Number(m[1]) * 60 + Number(m[2]);
}

// Both evaluators read an end of 24:00 as minute 1440, which the clock never reaches.
const parseEnd = (s: string) => /^24:00?$/.test(s) ? END_OF_DAY : parseTime(s);

const pad = (n: number) => String(n).padStart(2, "0");
const formatTime = (minutes: number) => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

// ==== decode

function decodeDay(stored: unknown): DaySchedule {
    if (!Array.isArray(stored) || stored.length === 0) return {mode: "allDay", rows: []};

    const spans: [number, number][] = [];
    const invalid: ScheduleRow[] = [];
    for (const slot of stored) {
        const rawStart = typeof slot?.start_time === "string" ? slot.start_time : "";
        const rawEnd = typeof slot?.end_time === "string" ? slot.end_time : "";
        const start = parseTime(rawStart);
        const end = parseEnd(rawEnd);
        if (start === null || end === null) {
            invalid.push({start: rawStart, end: rawEnd, toEndOfDay: false});
        } else if (start === end) {
            invalid.push({start: formatTime(start), end: formatTime(end), toEndOfDay: false});
        } else if (start > end) {
            if (end >= 2) spans.push([0, end]);
            if (END_OF_DAY - start >= 2) spans.push([start, END_OF_DAY]);
        } else if (end - start >= 2) {
            spans.push([start, end]);
        }
        // What is left is a 1-minute marker.
    }

    // Truly overlapping spans merge, which shows the same minutes; touching spans stay as entered.
    spans.sort((a, b) => a[0] - b[0]);
    const merged: [number, number][] = [];
    for (const [start, end] of spans) {
        const last = merged[merged.length - 1];
        if (last && start < last[1]) last[1] = Math.max(last[1], end);
        else merged.push([start, end]);
    }

    if (merged.length === 0 && invalid.length === 0) return {mode: "hidden", rows: []};
    if (invalid.length === 0 && merged.length === 1 && merged[0][0] === 0 && merged[0][1] === END_OF_DAY) {
        return {mode: "allDay", rows: []};
    }
    const rows = merged.map(([start, end]) => ({start: formatTime(start), end: formatTime(end), toEndOfDay: end === END_OF_DAY}));
    return {mode: "hours", rows: [...rows, ...invalid]};
}

export function decodeSchedule(availability: Availability | null | undefined): ScheduleForm {
    const days = {} as Record<Day, DaySchedule>;
    DAYS.forEach(day => days[day] = decodeDay(availability?.[day]));
    return {enabled: DAYS.some(day => days[day].mode !== "allDay"), days};
}

// ==== validate

const NO_ROWS = "Добавьте интервал или выберите «Весь день» или «Не показывать»";
const EMPTY_ROW = "Укажите время начала и окончания или удалите интервал";
const TO_MIDNIGHT = "Чтобы показывать до полуночи, отметьте «до конца дня»";
const TOO_SHORT = "Интервал должен длиться не меньше 2 минут";
const NOTHING_SHOWN = "Раздел не будет показываться ни в один день";

function overnightMessage(day: Day, start: number, end: number, next: Day, nextMode: DayMode): string {
    const head = `Интервал не может переходить через полночь. Укажите ${DAY_SHORT[day]} ${formatTime(start)} – до конца дня`;
    const tail = `00:00 – ${formatTime(end)}`;
    const byNextMode: Record<DayMode, string> = {
        allDay: `${head} — в ${DAY_SHORT[next]} раздел и так показывается весь день`,
        hidden: `${head} и включите ${DAY_SHORT[next]} «По часам» ${tail}`,
        hours: `${head} и добавьте в ${DAY_SHORT[next]} интервал ${tail}`,
    };
    return byNextMode[nextMode];
}

// A row's minutes, or the message that blocks saving it.
function checkRow(row: ScheduleRow, day: Day, next: Day, nextMode: DayMode): string | [number, number] {
    if (row.start === "" || (!row.toEndOfDay && row.end === "")) return EMPTY_ROW;
    const start = parseTime(row.start);
    if (start === null) return `Время указано неверно (${row.start}) — исправьте или удалите интервал`;
    const end = row.toEndOfDay ? END_OF_DAY : parseTime(row.end);
    if (end === null) return `Время указано неверно (${row.end}) — исправьте или удалите интервал`;
    // Ending at 00:00 or 00:01 means "until midnight"; a next-day tail that short would be rejected itself.
    if (end < start) return end <= 1 ? TO_MIDNIGHT : overnightMessage(day, start, end, next, nextMode);
    // A 1-minute row would store as the "not shown" marker.
    if (end - start < 2) return TOO_SHORT;
    return [start, end];
}

export function validateSchedule(form: ScheduleForm): { errors: ScheduleError[], warning: string | null } {
    if (!form.enabled) return {errors: [], warning: null};

    const errors: ScheduleError[] = [];
    DAYS.forEach((day, i) => {
        const {mode, rows} = form.days[day];
        if (mode !== "hours") return;
        if (rows.length === 0) {
            errors.push({day, row: null, message: NO_ROWS});
            return;
        }

        const next = DAYS[(i + 1) % DAYS.length];
        const spans: { row: number, start: number, end: number }[] = [];
        rows.forEach((row, index) => {
            const checked = checkRow(row, day, next, form.days[next].mode);
            if (typeof checked === "string") errors.push({day, row: index, message: checked});
            else spans.push({row: index, start: checked[0], end: checked[1]});
        });

        // Touching rows are allowed, as the backend allows them.
        spans.sort((a, b) => a.start - b.start);
        let reach: typeof spans[number] | null = null;
        for (const span of spans) {
            if (reach && span.start < reach.end) {
                const text = (s: typeof span) => `${formatTime(s.start)}–${formatTime(s.end)}`;
                errors.push({day, row: span.row, message: `Интервалы пересекаются: ${text(reach)} и ${text(span)}`});
            }
            if (!reach || span.end > reach.end) reach = span;
        }
    });

    const nothingShown = DAYS.every(day => form.days[day].mode === "hidden");
    return {errors, warning: nothingShown ? NOTHING_SHOWN : null};
}

// ==== encode

function encodeDay({mode, rows}: DaySchedule): TimeSlot[] {
    if (mode === "allDay") return [];
    if (mode === "hidden") return [{start_time: "00:00", end_time: "00:01"}];
    return rows
        .map(row => [parseTime(row.start)!, row.toEndOfDay ? END_OF_DAY : parseTime(row.end)!])
        .sort((a, b) => a[0] - b[0])
        .map(([start, end]) => ({start_time: formatTime(start), end_time: formatTime(end)}));
}

// Only valid forms encode; every output passes the backend's availability validation.
export function encodeSchedule(form: ScheduleForm): Availability | null {
    if (validateSchedule(form).errors.length > 0) throw new Error("encodeSchedule: the schedule has errors");
    if (!form.enabled || DAYS.every(day => form.days[day].mode === "allDay")) return null;
    const availability: Availability = {timezone: TIMEZONE};
    DAYS.forEach(day => availability[day] = encodeDay(form.days[day]));
    return availability;
}

// ==== edit

// Copies a day's mode and rows over the weekdays (Mon–Fri) or every day.
export function copyDay(form: ScheduleForm, from: Day, to: "weekdays" | "all"): ScheduleForm {
    const source = form.days[from];
    const days = {...form.days};
    (to === "all" ? DAYS : DAYS.slice(0, 5)).forEach(day => {
        days[day] = {mode: source.mode, rows: source.rows.map(row => ({...row}))};
    });
    return {...form, days};
}
